import { useState, useRef } from "react";
import { Plus, Edit2, Trash2, Eye, Download, Printer, X } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Logo from "../assets/Logo.png";

export default function Quotations() {
    const [quotations, setQuotations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [currentPreview, setCurrentPreview] = useState(null);

    const [formData, setFormData] = useState({
        quotationNumber: `QT-${Date.now().toString().slice(-4)}`,
        quotationBy: "DSD Systems Pvt. Ltd.\B-08, H161 Sector-63, Noida, India - 201010",
        quotationTo: "Studio Den\n305, 3rd Floor Orion mall\nBengaluru, Karnataka, India - 560055",
        date: new Date().toISOString().split("T")[0],
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        items: [
            { id: Date.now(), description: "Basic Web Development", quantity: 10, rate: 10000, discount: 5 }
        ],
        terms: "This quotation is valid for 15 days from the date of issue.\nWork will commence after quotation approval.\nScope is limited to the items mentioned above.\nAdditional requirements will be charged separately.\nTaxes will be applicable as per government regulations.",
        notes: "This quotation is valid for 15 days from the date of issue. The scope of work is limited to the items mentioned above. Any additional requirements will be charged separately.",
        status: "DRAFT",
    });

    const previewRef = useRef();

    const calculateItemTotal = (item) => {
        const total = item.quantity * item.rate;
        const discountAmount = total * (item.discount / 100);
        return total - discountAmount;
    };

    const calculateSubtotal = () => {
        return formData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    };

    const calculateTotalDiscount = () => {
        return formData.items.reduce((sum, item) => sum + (item.quantity * item.rate * (item.discount / 100)), 0);
    };

    const calculateTotal = () => {
        return calculateSubtotal() - calculateTotalDiscount();
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [
                ...formData.items,
                { id: Date.now(), description: "", quantity: 1, rate: 0, discount: 0 }
            ]
        });
    };

    const updateItem = (id, field, value) => {
        setFormData({
            ...formData,
            items: formData.items.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        });
    };

    const removeItem = (id) => {
        setFormData({
            ...formData,
            items: formData.items.filter(item => item.id !== id)
        });
    };

    const handleAddQuotation = () => {
        if (formData.quotationNumber && formData.quotationTo && formData.items.length > 0) {
            const quotationData = {
                ...formData,
                total: calculateTotal(),
                id: editingId || Date.now()
            };

            if (editingId) {
                setQuotations(
                    quotations.map((q) =>
                        q.id === editingId ? quotationData : q
                    )
                );
                setEditingId(null);
            } else {
                setQuotations([...quotations, quotationData]);
            }

            resetForm();
            setShowModal(false);
        }
    };

    const resetForm = () => {
        setFormData({
            quotationNumber: `QT-${Date.now().toString().slice(-4)}`,
            quotationBy: "DSD Systems Pvt. Ltd.\B-08, H161 Sector-63, Noida, India - 201010",
            quotationTo: "Studio Den\n305, 3rd Floor Orion mall\nBengaluru, Karnataka, India - 560055",
            date: new Date().toISOString().split("T")[0],
            dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
            items: [
                { id: Date.now(), description: "Basic Web Development", quantity: 10, rate: 10000, discount: 5 }
            ],
            terms: "1. This quotation is valid for 15 days from the date of issue.\n 2. Work will commence after quotation approval.\n 3. Scope is limited to the items mentioned above.\n 4. Additional requirements will be charged separately.\n 5. Taxes will be applicable as per government regulations.",
            notes: "1. This quotation is valid for 15 days from the date of issue. The scope of work is limited to the items mentioned above. Any additional requirements will be charged separately.",
            status: "DRAFT",
        });
    };

    const handleEdit = (quotation) => {
        setFormData(quotation);
        setEditingId(quotation.id);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        setQuotations(quotations.filter((q) => q.id !== id));
    };

    const handlePreview = (quotation) => {
        setCurrentPreview(quotation);
        setShowPreviewModal(true);
    };

    const generatePDF = async (quotation) => {
        const input = previewRef.current;

        // Use html2canvas to capture the preview
        const canvas = await html2canvas(input, {
            scale: 2,
            useCORS: true,
            logging: false,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`${quotation.quotationNumber}.pdf`);
    };

    const numberToWords = (num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

        if (num === 0) return 'Zero Rupees Only';

        let words = '';
        let index = 0;

        const convertLessThanOneThousand = (n) => {
            if (n === 0) return '';

            let result = '';

            if (n >= 100) {
                result += ones[Math.floor(n / 100)] + ' Hundred ';
                n %= 100;
            }

            if (n >= 20) {
                result += tens[Math.floor(n / 10)] + ' ';
                n %= 10;
            }

            if (n >= 10) {
                result += teens[n - 10] + ' ';
                n = 0;
            }

            if (n > 0) {
                result += ones[n] + ' ';
            }

            return result.trim();
        };

        while (num > 0) {
            if (num % 1000 !== 0) {
                words = convertLessThanOneThousand(num % 1000) + ' ' + thousands[index] + ' ' + words;
            }
            num = Math.floor(num / 1000);
            index++;
        }

        return words.trim() + ' Rupees Only';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "DRAFT":
                return "bg-gray-200 text-gray-800";
            case "SENT":
                return "bg-blue-200 text-blue-800";
            case "ACCEPTED":
                return "bg-green-200 text-green-800";
            case "REJECTED":
                return "bg-red-200 text-red-800";
            default:
                return "bg-gray-200 text-gray-800";
        }
    };

    const PreviewComponent = ({ quotation }) => {
        const subtotal = quotation.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
        const totalDiscount = quotation.items.reduce((sum, item) => sum + (item.quantity * item.rate * (item.discount / 100)), 0);
        const total = subtotal - totalDiscount;

        return (
            <div
                ref={previewRef}
                className="bg-white p-8 w-[210mm] h-[297mm] mx-auto shadow-lg"
                style={{ width: '210mm', minHeight: '297mm' }}
            >
                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                    <div>
                        <img src={Logo} alt="DSD Systems Logo" className="h-16 mb-4" />
                        <h1 className="text-3xl font-bold text-gray-900">DSD SYSTEMS</h1>
                    </div>
                    <div className="text-right">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">QUOTATION</h2>
                        <p className="text-gray-600">#{quotation.quotationNumber}</p>
                    </div>
                </div>

                {/* Quotation by/to */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="font-bold text-gray-900 mb-2">Quotation by</h3>
                        <div className="text-gray-600 whitespace-pre-line">
                            {quotation.quotationBy}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 mb-2">Quotation to</h3>
                        <div className="text-gray-600 whitespace-pre-line">
                            {quotation.quotationTo}
                        </div>
                    </div>
                </div>

                <hr className="border-gray-300 mb-8" />

                {/* Date Info */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                        <p className="text-gray-600">
                            <span className="font-bold">Date:</span> {new Date(quotation.date).toLocaleDateString('en-GB')}
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-600">
                            <span className="font-bold">Due Date:</span> {new Date(quotation.dueDate).toLocaleDateString('en-GB')}
                        </p>
                    </div>
                </div>

                {/* Items Table */}
                <div className="mb-8">
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <div className="grid grid-cols-5 bg-gray-100 p-4 font-bold text-gray-900">
                            <div>Item Description</div>
                            <div>Quantity</div>
                            <div>Rate</div>
                            <div>Discount</div>
                            <div>Amount (After Discount)</div>
                        </div>
                        {quotation.items.map((item, index) => (
                            <div key={item.id} className="grid grid-cols-5 p-4 border-t border-gray-300">
                                <div className="text-gray-700">{item.description}</div>
                                <div className="text-gray-700">{item.quantity}</div>
                                <div className="text-gray-700">₹{item.rate.toLocaleString()}</div>
                                <div className="text-gray-700">{item.discount}%</div>
                                <div className="text-gray-700 font-semibold">
                                    ₹{calculateItemTotal(item).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Marketing Text */}
                <div className="bg-blue-50 p-4 rounded-lg mb-8">
                    <p className="text-gray-700 text-center">
                        We provide end-to-end IT services including web development, software solutions, and technical support — tailored to your business needs.</p>
                </div>

                {/* Terms and Notes */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="font-bold text-gray-900 mb-2">Terms and Conditions</h3>
                        <div className="text-gray-600 whitespace-pre-line text-sm">
                            {quotation.terms}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 mb-2">Additional Notes</h3>
                        <div className="text-gray-600 text-sm">
                            {quotation.notes}
                        </div>
                    </div>
                </div>

                {/* Attachments */}
                {/* <div className="mb-8">
                    <h3 className="font-bold text-gray-900 mb-2">Attachments</h3>
                    <div className="flex gap-2">
                        <div className="px-3 py-1 bg-gray-100 rounded text-gray-700">filename.png</div>
                        <div className="px-3 py-1 bg-gray-100 rounded text-gray-700">invoice@2x.png</div>
                        <div className="px-3 py-1 bg-gray-100 rounded text-gray-700">verified-bank@2x.png</div>
                    </div>
                </div> */}

                {/* Summary */}
                <div className="border-t border-gray-300 pt-8">
                    <div className="flex justify-end">
                        <div className="w-64">
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-700">Sub Total</span>
                                <span className="font-bold">₹{subtotal.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-700">Discount</span>
                                <span className="font-bold text-red-600">-₹{totalDiscount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2">
                                <span>Total Amount</span>
                                <span>₹{total.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <p className="text-gray-700">
                            <span className="font-bold">Invoice Total in Words:</span> {numberToWords(total)}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-8 border-t border-gray-300 text-center text-gray-600">
                    <p>For any enquiries, email us on sales@dsdspl.com or call us on +91 98765-43210</p>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Quotations & Estimates
                </h1>
                <button
                    onClick={() => {
                        setEditingId(null);
                        resetForm();
                        setShowModal(true);
                    }}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                >
                    <Plus className="w-5 h-5" />
                    New Quotation
                </button>
            </div>

            {/* Quotations Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                {quotations.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <p className="text-lg">No quotations yet. Create one to get started!</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Quotation #
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {quotations.map((quotation) => {
                                    const total = quotation.items?.reduce((sum, item) => {
                                        const itemTotal = item.quantity * item.rate;
                                        const discountAmount = itemTotal * (item.discount / 100);
                                        return sum + (itemTotal - discountAmount);
                                    }, 0) || 0;

                                    return (
                                        <tr
                                            key={quotation.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                                        >
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                                                {quotation.quotationNumber}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                                {quotation.quotationTo.split('\n')[0]}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                                {new Date(quotation.date).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">
                                                ₹{total.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                                                        quotation.status
                                                    )}`}
                                                >
                                                    {quotation.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handlePreview(quotation)}
                                                        className="text-green-600 hover:text-green-800 dark:text-green-400 transition"
                                                        title="Preview"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => generatePDF(quotation)}
                                                        className="text-purple-600 hover:text-purple-800 dark:text-purple-400 transition"
                                                        title="Download PDF"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(quotation)}
                                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 transition"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(quotation.id)}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 transition"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create/Edit Quotation Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl shadow-xl my-8">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            {editingId ? "Edit Quotation" : "New Quotation"}
                        </h2>

                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Quotation Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.quotationNumber}
                                        onChange={(e) =>
                                            setFormData({ ...formData, quotationNumber: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="QT-001"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) =>
                                            setFormData({ ...formData, status: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="DRAFT">Draft</option>
                                        <option value="SENT">Sent</option>
                                        <option value="ACCEPTED">Accepted</option>
                                        <option value="REJECTED">Rejected</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) =>
                                            setFormData({ ...formData, date: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.dueDate}
                                        onChange={(e) =>
                                            setFormData({ ...formData, dueDate: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Quotation By
                                    </label>
                                    <textarea
                                        value={formData.quotationBy}
                                        onChange={(e) =>
                                            setFormData({ ...formData, quotationBy: e.target.value })
                                        }
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Your company address"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Quotation To
                                    </label>
                                    <textarea
                                        value={formData.quotationTo}
                                        onChange={(e) =>
                                            setFormData({ ...formData, quotationTo: e.target.value })
                                        }
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Customer address"
                                    />
                                </div>
                            </div>

                            {/* Items Section */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Items</h3>
                                    <button
                                        type="button"
                                        onClick={addItem}
                                        className="flex items-center gap-2 text-sm bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Item
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {formData.items.map((item, index) => (
                                        <div key={item.id} className="grid grid-cols-12 gap-4 items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <div className="col-span-5">
                                                <input
                                                    type="text"
                                                    value={item.description}
                                                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    placeholder="Item description"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    placeholder="Quantity"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <input
                                                    type="number"
                                                    value={item.rate}
                                                    onChange={(e) => updateItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    placeholder="Rate"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <input
                                                    type="number"
                                                    value={item.discount}
                                                    onChange={(e) => updateItem(item.id, 'discount', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    placeholder="Discount %"
                                                />
                                            </div>
                                            <div className="col-span-1">
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(item.id)}
                                                    className="text-red-600 hover:text-red-800 dark:text-red-400 transition"
                                                    title="Remove item"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="col-span-12 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Amount: ₹{calculateItemTotal(item).toLocaleString()}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Summary */}
                                <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-700 dark:text-gray-300">Sub Total:</span>
                                        <span className="font-bold">₹{calculateSubtotal().toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-700 dark:text-gray-300">Total Discount:</span>
                                        <span className="font-bold text-red-600">-₹{calculateTotalDiscount().toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-lg font-bold border-t border-gray-300 dark:border-gray-600 pt-2">
                                        <span>Total Amount:</span>
                                        <span>₹{calculateTotal().toLocaleString()}</span>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        In words: {numberToWords(calculateTotal())}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Terms and Conditions
                                    </label>
                                    <textarea
                                        value={formData.terms}
                                        onChange={(e) =>
                                            setFormData({ ...formData, terms: e.target.value })
                                        }
                                        rows="4"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Enter terms and conditions"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Additional Notes
                                    </label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) =>
                                            setFormData({ ...formData, notes: e.target.value })
                                        }
                                        rows="4"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Enter additional notes"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setEditingId(null);
                                    resetForm();
                                }}
                                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddQuotation}
                                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                            >
                                {editingId ? "Update Quotation" : "Create Quotation"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && currentPreview && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl shadow-xl my-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Preview Quotation #{currentPreview.quotationNumber}
                            </h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => generatePDF(currentPreview)}
                                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                                >
                                    <Download className="w-4 h-4" />
                                    Download PDF
                                </button>
                                <button
                                    onClick={() => setShowPreviewModal(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="border border-gray-300 rounded-lg overflow-auto max-h-[80vh]">
                            <PreviewComponent quotation={currentPreview} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}